import { useState } from "react"

import { Flex } from "@/atoms"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { SaveOutlined } from "@/icons"
import { useConfirmModal } from "@/molecules/ConfirmModal"
import { Heading } from "@/molecules/Heading"
import { useVirtualMachineConfig } from "@/organisms/Drawers/applications/inventory/useInventory"
import { ConfigurationBadge } from "@/organisms/Drawers/components/ConfigurationBadge/ConfigurationBadge"
import {
  DeleteConfirmModalConfiguration,
  FooterConfigurationTemplate
} from "@/organisms/Drawers/components/FooterConfigurationTemplate"
import { Form } from "@/organisms/Form"
import {
  CorrelatedImagesModalConfigurationTabVirtualMachineDrawer,
  ModalConfigurationTabVirtualMachineDrawer
} from "../ModalConfigurationTabVirtualMachineDrawer"

export const FooterConfigurationTabVirtualMachineDrawer = (props: {
  isRootSelected: boolean
  meta: { selected: number; correlated: number; total: number }
  context?: ReturnType<typeof useVirtualMachineConfig>
}) => {
  const { confirm } = useConfirmModal()
  const { form } = Form.useForm()
  const [open, setOpen] = useState(false)
  const { t } = useTranslation("translation")
  const { t: tFooter } = useTranslation("translation", {
    keyPrefix: "inventory.vm.drawer.configuration.footer"
  })
  const { t: tModals } = useTranslation("translation", {
    keyPrefix: "inventory.vm.drawer.configuration.modals"
  })
  const { intlNumber } = useNumberConvertor()
  const pathSelected = Form.useWatch("pathSelected", form)
  const { meta, context, isRootSelected } = props
  const { selected, correlated, total } = meta
  const noSelected = selected === 0
  const count = noSelected ? correlated : selected

  return (
    <FooterConfigurationTemplate
      disabledSelected={!pathSelected?.length}
      menu={{
        onClick: (info) => {
          confirm((attr) =>
            DeleteConfirmModalConfiguration({
              title: tModals("reset.title"),
              content: tModals("reset.content"),
              entityName: "reset-vm-to-project-correlations",
              onOk: async () => {
                try {
                  const pathSelected = form.getFieldValue("pathSelected")
                  const isSelectedReset =
                    info.key === "selected" && !isRootSelected

                  await context?.deleteConfig(
                    isSelectedReset ? pathSelected : undefined
                  )

                  form.resetFields()
                } catch (err: unknown) {
                  return Promise.reject(err)
                }
              },
              ...attr
            })
          )
        }
      }}
      aside={
        <Flex vertical flex={1}>
          <Heading
            level={6}
            title={tFooter(noSelected ? "pathCorrelated" : "pathSelected", {
              count,
              number: intlNumber(count),
              total: intlNumber(total)
            })}
          />
          <Flex align="center" gap={16}>
            <ConfigurationBadge
              type="vm"
              text={tFooter("correlated")}
              textColor="secondary"
            />
            <ConfigurationBadge
              type="stale"
              text={tFooter("stale")}
              textColor="secondary"
            />
          </Flex>
        </Flex>
      }
    >
      <Form.Footer
        okText={() => t("components.configuration.modals.save")}
        OkIcon={SaveOutlined}
        submitting={context?.isUpdating}
        saveGuard={() => {
          return {
            title: tModals("save.title"),
            content: tModals("save.content")
          }
        }}
        preventSubmit={(values: {
          project: { isCorrelatedToImage: boolean }
        }) => {
          const isCorrelatedToImage = values?.project?.isCorrelatedToImage

          if (!isCorrelatedToImage) return false

          setOpen(true)

          return true
        }}
      />
      <ModalConfigurationTabVirtualMachineDrawer
        open={open}
        okText={t("components.configuration.modals.detach")}
        onOk={() => {
          form.submit()
          setOpen(false)
        }}
        onCancel={() => setOpen(false)}
      >
        <CorrelatedImagesModalConfigurationTabVirtualMachineDrawer />
      </ModalConfigurationTabVirtualMachineDrawer>
    </FooterConfigurationTemplate>
  )
}
